import axios from "axios";

export interface Event {
  id: string;
  title: string;
  date: string;
  description: string;
  image: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  time?: string; // Legacy field, kept for backward compatibility
  eventLink?: string;
  teacher?: string;
  category?: string;
  registrationLink?: string;
}

// Sheet IDs voor verschillende omgevingen
const SHEET_CONFIG = {
  development: {
    id: "1qoVnl4udc5F3LpMnqeftOjMBM9VevonDMxrSRO2N6as",
    name: "Sheet1",
  },
  production: {
    id: "11mxHf2KDkRGLmxuMe2HE9NnCKuJ73uPsgjwWX1Ec6nU",
    name: "Sheet1",
  },
};

// Bepaal de huidige omgeving - use import.meta.env for Vite compatibility
const ENV = import.meta.env.MODE || "development";
console.log("Current environment for eventService:", ENV);

// Kolomindexen configuratie per omgeving (verschillende Google Forms hebben andere kolomvolgorde)
const COLUMN_INDEXES_CONFIG = {
  development: {
    TIMESTAMP: 0,
    TITLE: 1,
    DATE: 2,
    DESCRIPTION: 3,
    IMAGE: 4,
    LOCATION: 5,
    TIME: 6,
    EVENT_LINK: 7,
    START_TIME: 8,
    END_TIME: 9,
    TEACHER: 10,
    CATEGORY: 11,
    REGISTRATION_LINK: 12
  },
  production: {
    TIMESTAMP: 0,
    TITLE: 1,
    DESCRIPTION: 2,
    DATE: 3,
    TIME: 4,
    EVENT_LINK: 5,
    LOCATION: 6,
    IMAGE: 7,
    START_TIME: 8,
    END_TIME: 9,
    TEACHER: 10,
    CATEGORY: 11,
    REGISTRATION_LINK: 12
  }
};

// Selecteer de juiste configuratie voor de huidige omgeving
const SHEET_ID = SHEET_CONFIG[ENV as keyof typeof SHEET_CONFIG].id;
const SHEET_NAME = SHEET_CONFIG[ENV as keyof typeof SHEET_CONFIG].name;
const COLUMN_INDEXES = COLUMN_INDEXES_CONFIG[ENV as keyof typeof COLUMN_INDEXES_CONFIG];

function formatDate(dateString: string): string {
  try {
    if (!dateString) return "";
    
    if (dateString.startsWith("Date(")) {
      const parts = dateString
        .replace("Date(", "")
        .replace(")", "")
        .split(",")
        .map(Number);

      // parts[1] from 'Date(Y,M,D)' is already 0-indexed (0=Jan, 5=Jun, etc.)
      // So, no need to subtract 1 for JavaScript's new Date() constructor.
      const date = new Date(parts[0], parts[1], parts[2]);
      return date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    // Handle DD-MM-YYYY format
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("nl-NL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    }

    // Fallback to default date parsing
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
    
    return dateString;
  } catch (error) {
    console.error("Error formatting date:", error, "Input was:", dateString);
    return dateString;
  }
}

function formatTime(timeString: string): string | undefined {
  try {
    if (!timeString) return undefined;
    
    if (timeString.startsWith("Date(")) {
      const parts = timeString
        .replace("Date(", "")
        .replace(")", "")
        .split(",")
        .map(Number);

      // Create a date object with the time components
      const date = new Date(1970, 0, 1, parts[3], parts[4], parts[5]);

      return date.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    
    // If it's already in a time format like "14:30", just return it
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    
    // Try to parse it as a full date and extract the time
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    
    return timeString;
  } catch (error) {
    console.error("Error formatting time:", error);
    return undefined;
  }
}

function formatUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;

  try {
    url = url.trim();

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    if (url.startsWith("www.")) {
      return `https://${url}`;
    }

    if (!url.includes(".")) {
      return undefined;
    }

    return `https://www.${url}`;
  } catch (error) {
    console.error("Error formatting URL:", error);
    return undefined;
  }
}

export async function fetchEvents(): Promise<Event[]> {
  try {
    console.log(`Using ${ENV} sheet configuration:`, { SHEET_ID, SHEET_NAME });

    const response = await axios.get(
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${SHEET_NAME}`
    );

    const data = JSON.parse(response.data.substring(47).slice(0, -2));
    console.log("Raw Google Sheets data:", data.table.rows);

    if (!data.table.rows) return [];

    // Define a type for Google Sheets row cell
    type GoogleSheetCell = { v: string | number | null | Date; f?: string };
    // Define a type for Google Sheets row
    type GoogleSheetRow = { c: GoogleSheetCell[] };

    return data.table.rows
      .filter((row: GoogleSheetRow) => row.c[COLUMN_INDEXES.TITLE]?.v)
      .map((row: GoogleSheetRow, index: number) => {
        console.log("Processing upcoming event row:", row);
        
        const dateStr = String(row.c[COLUMN_INDEXES.DATE]?.v || "");
        console.log("Date string:", dateStr);
        
        // Get all possible fields from the row
        const imageUrl = String(row.c[COLUMN_INDEXES.IMAGE]?.v || "");
        
        // Legacy time field (column 6)
        const timeStr = String(row.c[COLUMN_INDEXES.TIME]?.v || "");
        
        // New time fields (columns 8 & 9)
        const startTimeStr = row.c[COLUMN_INDEXES.START_TIME]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.START_TIME]?.v || "");
          
        const endTimeStr = row.c[COLUMN_INDEXES.END_TIME]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.END_TIME]?.v || "");
        
        // Links
        const eventLinkStr = row.c[COLUMN_INDEXES.EVENT_LINK]?.v === null ? 
          undefined : String(row.c[COLUMN_INDEXES.EVENT_LINK]?.v || "");
          
        const registrationLinkStr = row.c[COLUMN_INDEXES.REGISTRATION_LINK]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.REGISTRATION_LINK]?.v || "");
        
        // Other new fields
        const teacherStr = row.c[COLUMN_INDEXES.TEACHER]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.TEACHER]?.v || "");
          
        const categoryStr = row.c[COLUMN_INDEXES.CATEGORY]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.CATEGORY]?.v || "");
        
        const title = String(row.c[COLUMN_INDEXES.TITLE]?.v || "");
        
        return {
          id: index.toString(),
          title: title,
          date: formatDate(dateStr),
          description: String(row.c[COLUMN_INDEXES.DESCRIPTION]?.v || ""),
          image: imageUrl,
          location: row.c[COLUMN_INDEXES.LOCATION]?.v === null ? 
            undefined : String(row.c[COLUMN_INDEXES.LOCATION]?.v || ""),
          // Support both legacy time field and new start/end time fields
          time: timeStr ? formatTime(timeStr) : 
                (startTimeStr ? formatTime(startTimeStr) : undefined),
          startTime: startTimeStr ? formatTime(startTimeStr) : undefined,
          endTime: endTimeStr ? formatTime(endTimeStr) : undefined,
          eventLink: formatUrl(eventLinkStr),
          teacher: teacherStr || undefined,
          category: categoryStr || undefined,
          registrationLink: formatUrl(registrationLinkStr),
        };
      });
  } catch (error) {
    console.error("Error fetching events:", error);
    throw new Error(
      "Er is een probleem opgetreden bij het ophalen van de evenementen. Probeer het later opnieuw.",
      { cause: error }
    );
  }
}
